// intakeDesk.js — how the agent's property card reads the shared intake question list (core 2394/2404, surface 'desk').
//
// Owner 2026-09-24 (decision E): the Desk and the sentavus.com owner form ask from ONE definition; the Desk is migrated
// deliberately, keeping every existing answer and its provenance. So this step changes only WHICH fields the card shows
// for a kind × subtype × deal and WHICH are required — never how a value is saved.
//
// Two rules, both deliberate:
//  • A field that holds a value is always shown, whatever the definition says — nothing recorded is put out of an
//    agent's reach (the definition may later say "not for this kind" about a value captured before it existed).
//  • A question's `when` (e.g. "building series only for an old building") IS applied since 2026-10-12 (form audit I25:
//    the Desk and the owner form disagreed about which questions are live) — but only for the answers the card holds in
//    the catalogue's own terms: the building and condition rows, the deal, the kind and its subtype. A condition that
//    names anything else (an owner-only gate, an answer the card does not keep) is not judged, and the field is shown:
//    an agent card still errs on showing. A field that holds a value is shown whatever its `when` says (first rule).
//
// Desk field key (the card's `facts` state, = core_prepare FACT_KEYS name) → intake question key.
export const DESK_FIELD_QUESTION = {
  rooms: 'rooms', bedrooms: 'bedrooms', bathrooms: 'bathrooms', area_sqm: 'area', kitchen_area: 'kitchen_area',
  floor: 'floor', floors_total: 'floors_total', ceiling_height: 'ceiling_height', ceiling_variation: 'ceiling_variation',
  balcony_count: 'balcony_count', balcony_area_sqm: 'balcony_area', has_terrace: 'terrace',
  power_capacity_kw: 'power_kw', functional_zone: 'functional_zone', land_area_sqm: 'land_area',
  metro_station: 'metro_station', living_room_area_sqm: 'living_room_area', loggia_area_sqm: 'loggia_area',
  storeroom_area_sqm: 'storeroom_area', porch_area_sqm: 'porch_area', building_material: 'building_material',
  parking_deeded: 'parking_deeded', year_built: 'year_built', project: 'project',
  room_unit_kind: 'room_unit_kind', room_unit_rooms: 'room_unit_rooms', room_unit_area_sqm: 'room_unit_area',
  room_unit_occupants: 'room_unit_occupants',
}
// the condition ladder and the apartment class are shown as one block on the card
export const DESK_CONDITION_QUESTIONS = ['condition', 'apartment_class']

const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== ''

/**
 * @param {object|null} def   intake_definition_v2(kind, subtype, intent, 'desk'), or null while loading / unavailable
 * @param {object} facts      the card's facts state
 * @param {(key:string)=>boolean} fallback  the card's own pre-catalogue rule, used until a definition arrives
 * @param {object} [answers]  what the card holds in the catalogue's terms: { building, condition, intent, kind, subtype }
 */
// one `when` entry against one held answer — the shared reader's meaning (intake-answers.js condMatch): a list is "any of"
function heldOk(cond, v) {
  if (Array.isArray(cond)) return cond.includes(v)
  if (cond && typeof cond === 'object') {
    if (Array.isArray(cond.any)) return cond.any.includes(v)
    if (Array.isArray(cond.all)) return cond.all.every((x) => x === v)
    if (Array.isArray(cond.none)) return !cond.none.includes(v)
  }
  return true
}
export function deskFieldRules(def, facts, fallback, answers = {}) {
  const qs = def && Array.isArray(def.questions) ? def.questions : null
  const keys = qs ? new Set(qs.map((q) => q.key)) : null
  // false only when a condition names an answer the card HOLDS and that answer rules the question out
  const whenOk = (qKey) => {
    const q = qs.find((x) => x.key === qKey)
    if (!q || !q.when || typeof q.when !== 'object') return true
    for (const [k, cond] of Object.entries(q.when)) {
      const v = answers ? answers[k] : undefined
      if (v == null || v === '') continue                     // not held (or not said yet): not judged
      if (!heldOk(cond, v)) return false
    }
    return true
  }
  const req = qs ? new Set(qs.filter((q) => q.required).map((q) => q.key)) : new Set()
  const shown = (field) => {
    if (hasValue(facts?.[field])) return true
    const q = DESK_FIELD_QUESTION[field]
    if (!keys || !q) return fallback(field)
    return keys.has(q) && whenOk(q)
  }
  const required = (field) => req.has(DESK_FIELD_QUESTION[field])
  const conditionShown = keys ? DESK_CONDITION_QUESTIONS.some((k) => keys.has(k)) : null
  return { shown, required, conditionShown, ready: !!keys }
}

// ── The condition block: three single-choice rows, from the same definition as the owner form ─────────────
// Owner 2026-09-24 ("let's match those"). The card used to offer ONE slot for 14 buttons — "under construction",
// the frames and the eight apartment classes — so a class wiped a frame, "old/new building" had no place, and the
// renovation words (old / renovated / new renovation / in progress / never lived in) had no button at all. Now:
//   building  → cls_new_built | cls_old_built | cls_under_constr   (under construction only for sale — 2401/2402)
//   condition → the ladder, each rung offered only for the buildings its `when` names
//   class     → the 8-level class (0078/0786), a second answer beside condition
// Every answer is a feature tag, saved exactly as before (tags → commit_feature); only the rows changed.
// A frame or an unbuilt building has no apartment class (class = livable quality; the card's rule since 2026-07-10),
// so the class row is not offered then — unless a class is already recorded, which is never put out of reach.
// ⚠️ A FOURTH ROW, 2026-10-12 (form audit I07; owner: "split … occupancy into separate questions", "make the Desk match
// the new additions"). "Never lived in" was a rung of the condition ladder, so a flat could be "newly renovated" OR
// "never lived in", never both. It is its own one-button row now (catalogue question never_lived_in, core 3228/3229),
// and the condition row no longer offers it. The CODE is unchanged (occupancy_never_lived_in), so a property that
// already carries it shows it in the new row, and its condition row reads empty until an agent says how it is finished.
export const CONDITION_ROW_KEYS = ['building', 'condition', 'apartment_class', 'never_lived_in']
const OCCUPANCY_CODE = 'occupancy_never_lived_in'
const NO_CLASS_CONDITIONS = new Set(['frame_black', 'frame_white', 'frame_white_plus', 'frame_green'])

// Used only until the definition arrives (or if it cannot be read): the same codes, unfiltered.
export const CONDITION_FALLBACK = {
  building: ['cls_new_built', 'cls_old_built', 'cls_under_constr'],
  condition: ['frame_black', 'frame_white', 'frame_white_plus', 'frame_green', 'reno_in_progress', 'frame_needs_work',
    'reno_old', 'reno_renovated', 'reno_new'],
  apartment_class: ['class_1_incomplete', 'class_2_soviet', 'class_3_clean', 'class_4_econ_standard',
    'class_5_modern_comfort', 'class_6_premium', 'class_7_designer', 'class_8_ultra_lux'],
  never_lived_in: [OCCUPANCY_CODE],
}
export const CONDITION_CODES = new Set(Object.values(CONDITION_FALLBACK).flat())

// Which row a recorded code belongs to.
export function conditionRowOf(code) {
  for (const k of CONDITION_ROW_KEYS) if (CONDITION_FALLBACK[k].includes(code)) return k
  return null
}

// An option's `when` names earlier answers or the deal; a blank answer does not exclude (same as the shared reader's
// optionWhenOk), so an agent who has not said old/new yet still sees every rung.
function optionOk(when, answers, intent) {
  if (!when || typeof when !== 'object') return true
  for (const [k, allowed] of Object.entries(when)) {
    const v = k === 'intent' ? intent : answers[k]
    if (v == null || v === '') continue
    if (Array.isArray(allowed) && !allowed.includes(v)) return false
  }
  return true
}

/**
 * @param {object|null} def     intake_definition_v2(kind, subtype, intent, 'desk')
 * @param {{building?:string, condition?:string, apartment_class?:string}} answers  what the card holds now
 * @param {{intent?:string, recorded?:object, lang?:string, fallbackLabel?:(code:string)=>string}} opts
 *   recorded = the same shape, as prefilled from the property (always offered, never hidden)
 * @returns {Array<{key, label, required, value, options:Array<{code,label}>}>}
 */
export function deskConditionRows(def, answers = {}, { intent, recorded = {}, lang = 'ka', fallbackLabel = (c) => c } = {}) {
  const qs = def && Array.isArray(def.questions) ? def.questions : null
  const pick = (o) => (o && (o[`label_${lang}`] || o.label_ka || o.label_en)) || null
  const rows = []
  for (const key of CONDITION_ROW_KEYS) {
    const q = qs ? qs.find((x) => x.key === key) : null
    const rec = recorded[key] || ''
    const val = answers[key] || ''
    if (qs && !q && !rec && !val) continue                    // the definition does not ask it for this kind
    if (key === 'apartment_class' && !rec && !val
      && (NO_CLASS_CONDITIONS.has(answers.condition) || answers.building === 'cls_under_constr')) continue
    const opts = q && Array.isArray(q.options)
      ? q.options.filter((o) => !(key === 'condition' && o.code === OCCUPANCY_CODE) && optionOk(o.when, answers, intent)).map((o) => ({ code: o.code, label: pick(o) || fallbackLabel(o.code) }))
      : CONDITION_FALLBACK[key].map((code) => ({ code, label: fallbackLabel(code) }))
    for (const code of [rec, val]) {                          // a recorded or chosen answer is always on screen
      if (code && !opts.some((o) => o.code === code)) {
        const o = q && Array.isArray(q.options) ? q.options.find((x) => x.code === code) : null
        opts.push({ code, label: pick(o) || fallbackLabel(code) })
      }
    }
    rows.push({ key, label: pick(q) || null, required: !!(q && q.required), value: val, options: opts })
  }
  return rows
}

// After a building change, a condition that building does not offer is dropped (it would be a combination the
// owner form refuses). A recorded condition is left alone — the agent changes it deliberately, not by side effect.
export function conditionAfterBuilding(def, condition, building, intent, recordedCondition) {
  if (!condition || condition === recordedCondition) return condition
  const q = def && Array.isArray(def.questions) ? def.questions.find((x) => x.key === 'condition') : null
  const o = q && Array.isArray(q.options) ? q.options.find((x) => x.code === condition) : null
  if (!o) return condition
  return optionOk(o.when, { building }, intent) ? condition : ''
}

// Choosing a frame or "under construction" clears a class the agent picked in this session (a frame has no class);
// a class already recorded on the property stays until the agent removes it.
export function classAfterCondition(next, recordedClass) {
  if (!next.apartment_class || next.apartment_class === recordedClass) return next.apartment_class
  return NO_CLASS_CONDITIONS.has(next.condition) || next.building === 'cls_under_constr' ? '' : next.apartment_class
}

// ── Metro (owner 2026-09-24, relayed by the filter session): Tbilisi is the only city in Georgia with a metro, so a
// nearest-station question is asked only when the property is in Tbilisi. Same rule as the public search's `hasMetro`.
// Tbilisi districts carry the TBS_ prefix; PERI_ rows are the settlements around the city and have no metro. Without
// a district the card's chosen city decides. A station already recorded stays visible (deskFieldRules' rule).
export function placeHasMetro(districtCode, districts = [], city = null) {
  const code = String(districtCode || '')
  if (code) {
    if (code.startsWith('TBS_')) return true
    if (code.startsWith('PERI_')) return false
    const row = (districts || []).find((d) => d.code === code)
    return !!row && /^(tbilisi|თბილისი)$/i.test(String(row.city || '').trim())
  }
  return /^(tbilisi|თბილისი)$/i.test(String(city || '').trim())
}

// ── The apart-hotel block (core 2856, owner 2026-10-02) ────────────────────────────────────────────────────────────
// The unit's own apart-hotel terms — registered status, operator, brand, programme, income model, return, contract,
// own-use days, the fee outside the programme. The building's terms live on the complex and are shown, not asked, here.
// Services and facilities are NOT in this block: they are feature codes in the `building` category, which the card's
// feature panel already offers — asking them twice would render one fact in two places.
// ⚠️ Unlike the rest of the card, this block DOES apply each question's `when`: every answer it reads is held by the
// card in the catalogue's own terms (the facts below + the apart-hotel tag), so "the return figure only once its kind
// is stated" is enforced here exactly as on the owner form.
export const APARTHOTEL_FACT_KEYS = ['registered_status', 'hosp_brand', 'hosp_operator', 'hosp_stars', 'hosp_programme',
  'hosp_income_model', 'hosp_owner_share_pct', 'hosp_return_kind', 'hosp_return_pct', 'hosp_return_years',
  'hosp_contract_years', 'hosp_owner_use_days', 'hosp_fee_outside_sqm']

/**
 * @param {object|null} def   intake_definition_v2(kind, subtype, intent, 'desk')
 * @param {object} facts      the card's facts state
 * @param {{isAparthotel:boolean, lang?:string}} opts
 * @returns {Array<{key, control, label, hint, unit, min, max, value, options:Array<{code,label}>}>}
 */
export function deskAparthotelRows(def, facts = {}, { isAparthotel = false, lang = 'ka' } = {}) {
  const qs = def && Array.isArray(def.questions) ? def.questions : []
  if (!isAparthotel) return []
  const pick = (o, f = 'label') => (o && (o[`${f}_${lang}`] || o[`${f}_ka`] || o[`${f}_en`])) || null
  const answers = { aparthotel: true }
  for (const k of APARTHOTEL_FACT_KEYS) if (hasValue(facts[k])) answers[k] = String(facts[k])
  const whenOk = (when) => !when || Object.entries(when).every(([k, list]) => k in answers
    && list.map(String).includes(String(answers[k])))
  return qs
    .filter((q) => q.grp === 'aparthotel' && q.store && APARTHOTEL_FACT_KEYS.includes(q.store.claim))
    // a recorded value stays visible even when its `when` no longer holds — nothing on record is put out of reach
    .filter((q) => whenOk(q.when) || hasValue(facts[q.store.claim]))
    .map((q) => ({
      key: q.store.claim, control: q.control, label: pick(q) || q.key, hint: pick(q, 'hint'),
      unit: q.unit || null, min: q.min ?? null, max: q.max ?? null,
      value: facts[q.store.claim] ?? '',
      options: (q.options || []).map((o) => ({ code: o.code, label: pick(o) || o.code })),
    }))
}
